
import { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container, Card, Button, Row, Col, Table, Alert } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

import '../styles/ViewPurchaseOrder.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ViewPurchaseOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { po_number } = useParams();
  const { userData } = useContext(AuthContext);
  const [poDetails, setPoDetails] = useState(null);
  const [, setPoItems] = useState([]);
  const [filters, setFilters] = useState({
    index: '', sku: '', style: '', color: '', size: '', fit: '', material: '', quantity: '', available_stock_qty: '', unit_price: '', total_price: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchPODetails = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await axios.get(`${BASE_URL}/api/admin/po/get-purchase-order-details/${po_number}`, {
          params: { company_code: userData?.company_code }
        });
        if (response.data.success) {
          // For view mode, fetch available stock for each item
          const itemsWithStock = await Promise.all(
            (response.data.items || []).map(async item => {
              try {
                const stockRes = await axios.get(`${BASE_URL}/api/admin/stock/get-stock-summary`, {
                  params: {
                    company_code: userData?.company_code,
                    style_number: item.style_number,
                    sku: item.sku
                  }
                });
                return {
                  ...item,
                  available_stock_qty: stockRes.data?.stock_qty ?? '-'
                };
              } catch {
                return {
                  ...item,
                  available_stock_qty: '-'
                };
              }
            })
          );
          setPoDetails({ ...response.data, items: itemsWithStock });
          setPoItems(itemsWithStock);
        } else {
          setError('Failed to load purchase order details');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message);
      } finally {
        setIsLoading(false);
      }
    };
    if (userData?.company_code && po_number) fetchPODetails();
  }, [userData?.company_code, po_number]);

  if (isLoading) {
    return <Container className="mt-4"><Alert variant="info">Loading...</Alert></Container>;
  }

  if (error) {
    return <Container className="mt-4"><Alert variant="danger">{error}</Alert></Container>;
  }

  if (!poDetails) {
    return null;
  }

  return (
    <Container fluid className="purchase-order-details">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h4>Purchase Order Details - {po_number}</h4>
          <Button 
            variant="secondary" 
            onClick={() => navigate(-1)}
            title="Back to List"
            style={{ padding: '10px' }}
          >
            <FaArrowLeft />
            Back to List
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="mb-4">
            <Col md={6}>
              <h6>Header Information</h6>
              <Table bordered size="sm">
                <tbody>
                  <tr><th>PO Number:</th><td>{poDetails.header.po_number}</td></tr>
                  <tr><th>Supplier:</th><td>{poDetails.header.supplier_name}</td></tr>
                  <tr><th>Attention:</th><td>{poDetails.header.attention}</td></tr>
                  <tr><th>Delivery Date:</th><td>{poDetails.header.delivery_date ? new Date(poDetails.header.delivery_date).toLocaleDateString() : '-'}</td></tr>
                  <tr><th>Tolerance Limit (%):</th><td>{poDetails.header.tolerance_limit}</td></tr>
                  <tr>
                    <th>Status:</th>
                    <td
                      style={{
                        color:
                          poDetails.header.status === "Pending"
                            ? "#ff9800"
                            : poDetails.header.status === "Approved"
                            ? "#28a745"
                            : "#6c757d",
                        fontWeight: "600",
                      }}
                    >
                      {poDetails.header.status}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
            <Col md={6}>
              <h6>Summary</h6>
              <Table bordered size="sm">
                <tbody>
                  <tr><th>Total Items:</th><td>{poDetails.items ? poDetails.items.length : 0}</td></tr>
                  <tr><th>Total Quantity:</th><td>{poDetails.items ? poDetails.items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) : 0}</td></tr>
                  <tr><th>Total Amount:</th><td>${parseFloat(poDetails.total_amount || 0).toFixed(2)}</td></tr>
                  <tr><th>Delivery Date:</th><td>{new Date(poDetails.header.delivery_date).toLocaleDateString()}</td></tr>
                  <tr><th>Created Date:</th><td>{new Date(poDetails.header.created_at).toLocaleDateString()}</td></tr>
                </tbody>
              </Table>
            </Col>
          </Row>

          {poDetails.header.remark && (
            <Row className="mb-3">
              <Col>
                <h6>Remarks</h6>
                <p className="bg-light p-2 rounded">{poDetails.header.remark}</p>
              </Col>
            </Row>
          )}

          <Row>
            <Col>
              <h6>Items</h6>
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>SKU</th>
                      <th>Product</th>
                      <th>Color</th>
                      <th>Size</th>
                      <th>Fit</th>
                      <th>Material</th>
                      <th>Quantity</th>
                      <th>Available Stock </th>
                      <th>Unit Price</th>
                      <th>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Filter row */}
                    <tr>
                      <td>
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Search SKU"
                          value={filters.sku}
                          onChange={e => setFilters({ ...filters, sku: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Search Product"
                          value={filters.style}
                          onChange={e => setFilters({ ...filters, style: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Search Color"
                          value={filters.color}
                          onChange={e => setFilters({ ...filters, color: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Search Size"
                          value={filters.size}
                          onChange={e => setFilters({ ...filters, size: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Search Fit"
                          value={filters.fit}
                          onChange={e => setFilters({ ...filters, fit: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Search Material"
                          value={filters.material}
                          onChange={e => setFilters({ ...filters, material: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Qty"
                          value={filters.quantity}
                          onChange={e => setFilters({ ...filters, quantity: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Available"
                          value={filters.available_stock_qty}
                          onChange={e => setFilters({ ...filters, available_stock_qty: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Unit Price"
                          value={filters.unit_price}
                          onChange={e => setFilters({ ...filters, unit_price: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Total Price"
                          value={filters.total_price}
                          onChange={e => setFilters({ ...filters, total_price: e.target.value })}
                        />
                      </td>
                    </tr>

                    {/* Data rows - filtered */}
                    {((poDetails.items || []).filter((item, index) => {
                      const idx = (index + 1).toString();
                      const unitPriceStr = parseFloat(item.unit_price || 0).toFixed(2);
                      const totalPriceStr = parseFloat(item.total_price || 0).toFixed(2);

                      const checks = [
                        !filters.index || idx.includes(filters.index.trim()),
                        !filters.sku || (item.sku || '').toString().toLowerCase().includes(filters.sku.trim().toLowerCase()),
                        !filters.style || (item.style_name || '').toLowerCase().includes(filters.style.trim().toLowerCase()),
                        !filters.color || (item.color_name || '').toLowerCase().includes(filters.color.trim().toLowerCase()),
                        !filters.size || (item.size_name || '').toLowerCase().includes(filters.size.trim().toLowerCase()),
                        !filters.fit || (item.fit_name || '').toLowerCase().includes(filters.fit.trim().toLowerCase()),
                        !filters.material || (item.material_name || '').toLowerCase().includes(filters.material.trim().toLowerCase()),
                        !filters.quantity || (item.quantity !== undefined && item.quantity !== null && item.quantity.toString().includes(filters.quantity.trim())),
                        !filters.available_stock_qty || (item.available_stock_qty !== undefined && item.available_stock_qty !== null && item.available_stock_qty.toString().toLowerCase().includes(filters.available_stock_qty.trim().toLowerCase())),
                        !filters.unit_price || unitPriceStr.includes(filters.unit_price.replace('$', '').trim()),
                        !filters.total_price || totalPriceStr.includes(filters.total_price.replace('$', '').trim())
                      ];

                      return checks.every(Boolean);
                    })).map((item, index) => (
                      <tr key={item.item_id || index}>
                        <td>{index + 1}</td>
                        <td>{item.sku}</td>
                        <td>{item.style_name || 'N/A'}</td>
                        <td>{item.color_name || 'N/A'}</td>
                        <td>{item.size_name || 'N/A'}</td>
                        <td>{item.fit_name || 'N/A'}</td>
                        <td>{item.material_name || 'N/A'}</td>
                        <td className='number-col'>{item.quantity}</td>
                        <td className='number-col'>{item.available_stock_qty !== undefined && item.available_stock_qty !== null ? item.available_stock_qty : '-'}</td>
                        <td className='number-col'>${parseFloat(item.unit_price || 0).toFixed(2)}</td>
                        <td className='number-col'>${parseFloat(item.total_price || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="table-info">
                      <th colSpan="10" className="text-end">Total:</th>
                      <th className='number-col'>${parseFloat(poDetails.total_amount || 0).toFixed(2)}</th>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}
